/**
 * Event catalog viewer - /settings/webhooks/catalog.
 *
 * Read-only browser for every platform event a tenant can subscribe
 * to. Grouped by entity, searchable, with an expand-to-show JSON
 * Schema preview per event. Backed by the same usePulseEventTypes
 * hook the create-flow event picker uses, so anything an admin sees
 * here is exactly what they can attach to a webhook subscription.
 *
 * Tenant-defined events (future Tier 2 entity events) will land in
 * this list automatically once they're materialised - the catalog
 * endpoint already merges them with the platform catalog.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowLeft, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'

import {
  groupEventsByEntity,
  usePulseEventTypes,
  type PulseEventType,
} from '@/admin/hooks/use-pulse-events'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/lib/utils'

export function PulseEventsCatalogPage() {
  const navigate = useNavigate()
  const events = usePulseEventTypes()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const rows = events.data ?? []
    if (!query.trim()) return rows
    const needle = query.trim().toLowerCase()
    return rows.filter(
      (e) =>
        e.id.toLowerCase().includes(needle) ||
        e.label.toLowerCase().includes(needle) ||
        e.description.toLowerCase().includes(needle),
    )
  }, [events.data, query])

  const groups = useMemo(() => groupEventsByEntity(filtered), [filtered])

  if (events.isLoading) {
    return (
      <div className="mt-8 flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="mt-8 max-w-[900px]">
      {/* Header */}
      <button
        type="button"
        onClick={() => navigate('/settings/webhooks')}
        className="mb-3 inline-flex cursor-pointer items-center gap-1 text-[11px] text-[var(--ink-3)] hover:text-[var(--ink-2)]"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to Webhooks
      </button>
      <h2 className="text-[15px] font-medium text-[var(--ink)]">
        Event catalog
      </h2>
      <p className="mt-0.5 max-w-[640px] text-[12px] text-[var(--ink-3)]">
        Every event the platform fires. Subscribe to any of these from a
        webhook&apos;s configuration. Click a row to inspect the payload
        schema.
      </p>

      {/* Search */}
      <div className="mt-5">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search events by name, key, or description..."
          className="max-w-[420px]"
        />
        <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">
          {filtered.length} event{filtered.length === 1 ? '' : 's'} across{' '}
          {groups.length} entit{groups.length === 1 ? 'y' : 'ies'}.
        </p>
      </div>

      {/* Groups */}
      <div className="mt-6 space-y-6">
        {groups.length === 0 ? (
          <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-6 py-10 text-center">
            <p className="text-[12.5px] text-[var(--ink-2)]">No matches.</p>
            <p className="mt-1 text-[11.5px] text-[var(--ink-3)]">
              Try a different search term.
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.key}>
              <h3 className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                {g.label}
              </h3>
              <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
                {g.events.map((e) => (
                  <EventRow key={e.id} event={e} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Per-event row
// ---------------------------------------------------------------------------

function EventRow({ event }: { event: PulseEventType }) {
  const [expanded, setExpanded] = useState(false)
  const hasSchema =
    event.payload_schema && Object.keys(event.payload_schema).length > 0

  return (
    <div className="border-t-[0.5px] border-t-[var(--rule-light)] first:border-t-0">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-subtle)]"
      >
        <span className="mt-0.5 shrink-0 text-[var(--ink-3)]">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-medium text-[var(--ink)]">
              {event.label}
            </span>
            <FamilyBadge family={event.family} />
            {event.status !== 'ACTIVE' && (
              <Badge variant="outline" className="text-[var(--ink-3)]">
                {event.status.toLowerCase()}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 truncate font-mono text-[11px] text-[var(--ink-3)]">
            {event.id}
          </p>
          {event.description && (
            <p className="mt-1 text-[11.5px] text-[var(--ink-2)]">
              {event.description}
            </p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t-[0.5px] border-t-[var(--rule-light)] bg-[var(--bg-subtle)] px-4 py-3">
          <div className="mb-1 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            Payload schema
          </div>
          {hasSchema ? (
            <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-all rounded-[4px] bg-[var(--bg)] p-2.5 font-mono text-[10.5px] leading-snug text-[var(--ink-2)]">
              {JSON.stringify(event.payload_schema, null, 2)}
            </pre>
          ) : (
            <p className="text-[11.5px] text-[var(--ink-3)]">
              No schema published yet. The payload follows the envelope
              shape with a free-form <code className="font-mono">data</code>{' '}
              block.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function FamilyBadge({ family }: { family: string }) {
  // A=lifecycle, B=workflow, C=issues, D=rules, ... - cheap colour map.
  const map: Record<string, { label: string; tone: string }> = {
    A: { label: 'Lifecycle', tone: 'text-[var(--green)]' },
    B: { label: 'Workflow', tone: 'text-[var(--ink-2)]' },
    C: { label: 'Issue', tone: 'text-[var(--gold-ink)]' },
    D: { label: 'Rule', tone: 'text-[var(--ink-2)]' },
  }
  const meta = map[family] ?? { label: family, tone: 'text-[var(--ink-3)]' }
  return (
    <Badge
      variant="outline"
      className={cn('border-[var(--rule)] bg-[var(--bg-subtle)]', meta.tone)}
    >
      {meta.label}
    </Badge>
  )
}
